// src/pages/EditFinalSummary.tsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

// Interface for dealer reports
interface Report {
  id: number;
  created_at: string;
}

// Interface for final summary data from the API
interface FinalSummaryData {
  id: number | null;
  overallRating: string;
  keyTakeaways: string;
  representativeName: string;
  date: string;
}

const EditFinalSummary: React.FC = () => {
  const { id } = useParams<{ id: string }>(); // dealer id
  const navigate = useNavigate();

  const [formData, setFormData] = useState<FinalSummaryData>({
    id: null,
    overallRating: "",
    keyTakeaways: "",
    representativeName: "",
    date: ""
  });

  // New state variables for report integration.
  const [reports, setReports] = useState<Report[]>([]);
  const [newestReportId, setNewestReportId] = useState<number | null>(null);

  // Step 1: Fetch the dealer's reports; if none exist, create one.
  useEffect(() => {
    fetch(`/api/dealers/${id}/reports`)
      .then((res) => res.json())
      .then((data: Report[]) => {
        if (Array.isArray(data) && data.length > 0) {
          // Assume reports are sorted descending by created_at.
          setReports(data);
          setNewestReportId(data[0].id);
        } else {
          // No report exists; create one.
          fetch(`/api/dealers/${id}/reports`, {
            method: "POST",
            headers: { "Content-Type": "application/json" }
          })
            .then((res) => res.json())
            .then((newReport: Report) => setNewestReportId(newReport.id))
            .catch((err) => console.error("Error creating report", err));
        }
      })
      .catch((err) => console.error("Error fetching reports", err));
  }, [id]);

  // Step 2: With newestReportId available, fetch final summary data via report‑centric endpoint.
  useEffect(() => {
    if (!newestReportId) return;
    fetch(`/api/reports/${newestReportId}/final-summary`)
      .then((res) => res.json())
      .then((data) => {
        // Format the summary date (if available) so that it contains only the date portion (YYYY-MM-DD)
        const formattedDate = data.summary_date
          ? new Date(data.summary_date).toISOString().split("T")[0]
          : "";
        setFormData({
          id: data.id || null,
          overallRating: data.overall_rating || "",
          keyTakeaways: data.key_takeaways || "",
          representativeName: data.representative_name || "",
          date: formattedDate
        });
      })
      .catch((err) => console.error("Error fetching final summary", err));
  }, [newestReportId, id]);

const handleChange = (
  e: React.ChangeEvent<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >
) => {
  setFormData({ ...formData, [e.target.name]: e.target.value });
};


  // Step 3: On form submission, update the final summary data via the report‑centric endpoint.
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Build payload; note that keys may be mapped as required by your backend.
    const payload = {
      dealer_id: id,
      overallRating: formData.overallRating,
      keyTakeaways: formData.keyTakeaways,
      representativeName: formData.representativeName,
      summaryDate: formData.date
    };

    if (newestReportId) {
      if (formData.id) {
        // Update existing final summary record.
        fetch(`/api/reports/${newestReportId}/final-summary`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: formData.id, ...payload })
        })
          .then(() => navigate(`/dealer/${id}`))
          .catch((err) => console.error("Error updating final summary", err));
      } else {
        // Create new final summary record.
        fetch(`/api/reports/${newestReportId}/final-summary`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        })
          .then(() => navigate(`/dealer/${id}`))
          .catch((err) => console.error("Error creating final summary", err));
      }
    } else {
      console.error("No report found for this dealer.");
    }
  };

  // UI styling remains unchanged.
  const containerStyle: React.CSSProperties = { backgroundColor: "#F0F5F0", minHeight: "100vh", padding: "20px" };
  const cardStyle: React.CSSProperties = {
    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    maxWidth: "800px",
    margin: "30px auto",
    padding: "30px"
  };
  const headerStyle: React.CSSProperties = {
    textAlign: "center",
    color: "#e41e26",
    borderBottom: "2px solid #e41e26",
    paddingBottom: "10px",
    marginBottom: "20px",
    fontSize: "24px"
  };
  const labelStyle: React.CSSProperties = { display: "block", marginBottom: "5px", fontWeight: "bold" };
  const inputStyle: React.CSSProperties = { width: "100%", padding: "8px", marginBottom: "10px", borderRadius: "4px", border: "1px solid #ccc" };
  const buttonStyle: React.CSSProperties = { padding: "5px 10px", backgroundColor: "#e41e26", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", marginRight: "10px" };

  return (
    <div style={containerStyle}>
      <Navbar />
      <div style={cardStyle}>
        <h1 style={headerStyle}>Edit Final Summary</h1>
        <form onSubmit={handleSubmit}>
          <label style={labelStyle}>Overall Rating (1–5):</label>
		 <select
		   name="overallRating"
		   style={inputStyle}
		   value={formData.overallRating}
		   onChange={handleChange}
		 >
		   <option value="">Select rating</option>
		   {[1, 2, 3, 4, 5].map((n) => (
			 <option key={n} value={String(n)}>
			   {n}
			 </option>
		   ))}
		 </select>
          <label style={labelStyle}>Key Takeaways & Next Steps:</label>
          <textarea
            name="keyTakeaways"
            style={inputStyle}
            value={formData.keyTakeaways}
            onChange={handleChange}
          />
          <label style={labelStyle}>Representative Name:</label>
          <input
            type="text"
            name="representativeName"
            style={inputStyle}
            value={formData.representativeName}
            onChange={handleChange}
          />
          <label style={labelStyle}>Date:</label>
          <input
            type="date"
            name="date"
            style={inputStyle}
            value={formData.date}
            onChange={handleChange}
          />
          <div style={{ marginTop: "20px" }}>
            <button type="submit" style={buttonStyle}>Save</button>
            <button type="button" style={buttonStyle} onClick={() => navigate(`/dealer/${id}`)}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditFinalSummary;
